<script lang="ts">
  import { famousAt } from '../../core/famous';
  import { formatFactors, isPrime, isTwinPrime, primeIndex } from '../../core/numbers';
  import { fmt, ordinal } from '../lib/format';
  import { game } from '../lib/game.svelte';
  import { settings } from '../lib/settings.svelte';

  let {
    eyebrow = 'Position',
    caption: showCaption = true,
    hero = false,
  }: { eyebrow?: string; caption?: boolean; hero?: boolean } = $props();

  let numEl: HTMLElement;
  let last = game.s.position;

  const n = $derived(game.s.position);
  const prime = $derived(isPrime(n));
  const twin = $derived(prime && isTwinPrime(n));
  const famous = $derived(famousAt(n));

  // With the caption hidden (V3's opening), the number stays plain ink: no colour coding
  // until the game has explained what the colours mean.
  const kind = $derived(
    !showCaption ? 'plain' : prime ? (twin ? 'twin' : 'prime') : famous.length ? 'famous' : 'plain',
  );

  const caption = $derived.by(() => {
    if (prime) return `${twin ? 'Twin prime' : 'Prime'} · the ${ordinal(primeIndex(n))}`;
    if (famous.length) return famous.map((f) => f.name).join(' · ');
    if (n === 0 || n === 1) return 'Neither prime nor composite';
    if (n === -1) return 'The negative unit';
    return formatFactors(n);
  });

  // A short slide on every change, in the direction of travel. Uses WAAPI so rapid taps
  // simply restart the animation instead of stacking DOM nodes.
  $effect(() => {
    const next = n;
    if (!numEl || next === last || settings.reducedMotion) {
      last = next;
      return;
    }
    const dir = next > last ? 1 : -1;
    last = next;
    numEl.animate(
      [
        { transform: `translateY(${dir * 0.09}em)`, opacity: 0.4 },
        { transform: 'translateY(0)', opacity: 1 },
      ],
      { duration: 170, easing: 'cubic-bezier(0.22, 1, 0.36, 1)' },
    );
    if (prime || famous.length) {
      numEl.animate([{ transform: 'scale(1.06)' }, { transform: 'scale(1)' }], {
        duration: 420,
        easing: 'cubic-bezier(0.34, 1.56, 0.64, 1)',
        composite: 'add',
      });
    }
  });
</script>

<div class="readout" class:hero data-kind={kind}>
  {#if eyebrow}<span class="eyebrow">{eyebrow}</span>{/if}
  <output class="value" bind:this={numEl} aria-live="off">{fmt(n)}</output>
  {#if showCaption}
    <p class="caption" aria-live="polite">
      {#if kind !== 'plain'}<span class="dot" aria-hidden="true"></span>{/if}
      <span class:mono={kind === 'plain'}>{caption}</span>
    </p>
  {/if}
</div>

<style>
  .readout {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: var(--space-1);
    min-width: 0;
    text-align: center;
  }

  .value {
    display: block;
    font-family: var(--font-num);
    font-weight: var(--num-weight);
    font-variant-numeric: tabular-nums lining-nums;
    font-size: clamp(3.25rem, 16vw, 7rem);
    line-height: 1;
    letter-spacing: -0.03em;
    color: var(--ink);
    transition:
      color var(--t) var(--ease-out),
      text-shadow var(--t-slow) var(--ease-out);
    will-change: transform;
  }

  [data-kind='prime'] .value {
    color: var(--prime);
    text-shadow: 0 0 0.5em color-mix(in srgb, var(--prime) 22%, transparent);
  }
  [data-kind='twin'] .value {
    color: var(--twin);
    text-shadow: 0 0 0.5em color-mix(in srgb, var(--twin) 22%, transparent);
  }
  [data-kind='famous'] .value {
    color: var(--famous);
  }

  .hero .value {
    font-size: clamp(4.5rem, 26vw, 11rem);
  }

  .caption {
    min-height: 1.5em;
    display: inline-flex;
    align-items: center;
    gap: var(--space-2);
    font-size: var(--text-sm);
    font-weight: 600;
    color: var(--ink-2);
    max-width: 100%;
  }

  .caption .mono {
    font-family: var(--font-mono);
    font-weight: 500;
    color: var(--ink-3);
  }

  .dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    flex: none;
  }
  [data-kind='prime'] .caption {
    color: var(--prime);
  }
  [data-kind='prime'] .dot {
    background: var(--prime);
  }
  [data-kind='twin'] .caption {
    color: var(--twin);
  }
  [data-kind='twin'] .dot {
    background: var(--twin);
  }
  [data-kind='famous'] .caption {
    color: var(--famous);
  }
  [data-kind='famous'] .dot {
    background: var(--famous);
  }
</style>
